import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { Button } from "../../UI/MUIButton";
import { isMigrationPreviewValid } from "../../services/IntegrationApi";
import { ImportMigrationWizardState } from "./useImportMigrationWizard";
import { FROM_TYPE_LABELS, isWizardFlow, TOTAL_STEPS } from "./types";
import { WizardHeader } from "./WizardHeader";
import { ConfigureStep } from "./ConfigureStep";
import { UploadStep } from "./UploadStep";
import { OptionalCsvStep } from "./OptionalCsvStep";
import { PreviewStep } from "./PreviewStep";
import { ConfirmStep } from "./ConfirmStep";

interface MigrationWizardBodyProps {
  wizard: ImportMigrationWizardState;
}

export const MigrationWizardBody: React.FC<MigrationWizardBodyProps> = ({ wizard }) => {
  const fromTypeLabel = FROM_TYPE_LABELS[wizard.fromType];
  const headerLabel = wizard.isConnect
    ? "Connect Integration"
    : wizard.targetType
      ? `${fromTypeLabel} → ${wizard.targetType} Migration`
      : `${fromTypeLabel} Migration`;

  const renderStep = () => {
    if (wizard.optionsLoading && wizard.step === 0) {
      return (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      );
    }
    switch (wizard.step) {
      case 0:
        return (
          <ConfigureStep
            targetOptions={wizard.targetOptions}
            targetType={wizard.targetType}
            onTargetChange={wizard.setTargetType}
            flowKind={wizard.flowKind}
            selectedMigration={wizard.selectedMigration}
            portfolios={wizard.portfolios}
            entrataUsers={wizard.entrataUsers}
            portfolioId={wizard.portfolioId}
            onPortfolioChange={wizard.setPortfolioId}
            entrataUserId={wizard.entrataUserId}
            onEntrataUserChange={wizard.setEntrataUserId}
            entrataPropertyId={wizard.entrataPropertyId}
            onEntrataPropertyIdChange={wizard.setEntrataPropertyId}
            downloadingTemplate={wizard.downloadingTemplate}
            onDownloadTemplate={wizard.downloadTemplate}
            legacyFields={wizard.legacyFields}
            onLegacyFieldChange={wizard.setLegacyField}
          />
        );
      case 1:
        if (wizard.flowKind === "tenantkey") {
          return (
            <OptionalCsvStep
              file={wizard.file}
              error={wizard.csvError}
              onSelect={wizard.selectCsvFile}
              onRemove={wizard.clearCsvFile}
            />
          );
        }
        return <UploadStep file={wizard.file} onFile={wizard.setFile} />;
      case 2:
        if (wizard.flowKind === "tenantkey") {
          return <ConfirmStep targetType={wizard.targetType} file={wizard.file} />;
        }
        return <PreviewStep preview={wizard.preview} />;
      default:
        return null;
    }
  };

  return (
    <>
      <WizardHeader
        title={headerLabel}
        step={wizard.step}
        totalSteps={TOTAL_STEPS}
        showStepBadge={isWizardFlow(wizard.flowKind)}
      />
      <Box p={3}>{renderStep()}</Box>
    </>
  );
};

interface MigrationWizardActionsProps {
  wizard: ImportMigrationWizardState;
  onCancel: () => void;
}

export const MigrationWizardActions: React.FC<MigrationWizardActionsProps> = ({
  wizard,
  onCancel,
}) => {
  const previewValid = wizard.preview != null && isMigrationPreviewValid(wizard.preview);

  // Legacy mode keeps the single-screen Cancel | Migrate action bar
  if (wizard.flowKind === "legacy") {
    return (
      <>
        <Button variant="outlined" onClick={onCancel}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          loading={wizard.legacyMigrating}
          disabled={!wizard.targetType || !wizard.legacyIsValid}
          onClick={wizard.runLegacyMigration}
        >
          {wizard.isConnect ? "Connect" : "Migrate"}
        </Button>
      </>
    );
  }

  // ACC → TenantKey: configure, optional CSV upload, confirm. The upload step is
  // skippable, so "Next →" stays enabled with or without a file.
  if (wizard.flowKind === "tenantkey") {
    switch (wizard.step) {
      case 0:
        return (
          <>
            <Button variant="outlined" onClick={onCancel}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!wizard.targetType}
              onClick={() => wizard.setStep(1)}
            >
              Next →
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <Button variant="outlined" onClick={() => wizard.setStep(0)}>← Back</Button>
            <Button color="primary" variant="contained" onClick={() => wizard.setStep(2)}>
              Next →
            </Button>
          </>
        );
      case 2:
        return (
          <>
            <Button variant="outlined" onClick={() => wizard.setStep(1)}>← Back</Button>
            <Button
              color="primary"
              variant="contained"
              loading={wizard.executing}
              onClick={wizard.runTenantKeyMigration}
            >
              {wizard.isConnect ? "Connect" : "Migrate"}
            </Button>
          </>
        );
      default:
        return null;
    }
  }

  switch (wizard.step) {
    case 0:
      return (
        <>
          <Button variant="outlined" onClick={onCancel}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!wizard.targetType || !wizard.isConfigComplete}
            onClick={wizard.goToUpload}
          >
            Next →
          </Button>
        </>
      );
    case 1:
      return (
        <>
          <Button variant="outlined" onClick={() => wizard.setStep(0)}>← Back</Button>
          <Button
            color="primary"
            variant="contained"
            loading={wizard.previewing}
            disabled={!wizard.file}
            onClick={wizard.runPreview}
          >
            Validate →
          </Button>
        </>
      );
    case 2:
      return (
        <>
          <Button variant="outlined" onClick={() => wizard.setStep(1)}>← Back</Button>
          {previewValid && (
            <Button
              color="error"
              variant="contained"
              loading={wizard.executing}
              onClick={wizard.runMigration}
            >
              {wizard.isConnect ? "Confirm Connection" : "Confirm Migration"}
            </Button>
          )}
        </>
      );
    default:
      return null;
  }
};
